<script lang="ts" setup>
import { computed, ref, useSlots, type Component } from 'vue';
import { Primitive } from 'radix-vue';
import { IconUi } from '../../icon';
import {
  chipSelectAvatar,
  chipSelectContentRecipe,
  chipSelectLabel,
  chipSelectLeadingRecipe,
  chipSelectRecipe,
  chipSelectSubLabelRecipe,
} from './chipSelectUiRecipe';

export interface ChipSelectProps {
  as?: string;
  size?: 'small' | 'large';
  selected?: boolean;
  disabled?: boolean;
  interactive?: boolean;
  elevated?: boolean;
  subLabel?: string;
  optionName?: string;
  leadingIcon?: Component | string;
  leadingAvatarSrc?: string;
  leadingAvatarAlt?: string;
}

const props = withDefaults(defineProps<ChipSelectProps>(), {
  as: 'button',
  size: 'small',
  selected: undefined,
  disabled: false,
  interactive: true,
  elevated: false,
});

const emit = defineEmits<{
  'update:selected': [value: boolean];
}>();

const slots = useSlots();

const isControlled = computed(() => props.selected !== undefined);

const internalSelected = ref(false);
const isSelected = computed(() =>
  isControlled.value ? Boolean(props.selected) : internalSelected.value,
);

const leadingVariant = computed(() => {
  if (props.leadingAvatarSrc) {
    return 'avatar' as const;
  }
  return props.leadingIcon ? ('icon' as const) : ('none' as const);
});

// The icon and the spacer are decorative; an avatar with an alt text is not.
const isLeadingDecorative = computed(
  () => !(leadingVariant.value === 'avatar' && props.leadingAvatarAlt),
);

const hasSubLabel = computed(
  () => props.size === 'large' && Boolean(props.subLabel || slots.sublabel),
);

const ariaLabel = computed(() => {
  if (!props.optionName || !props.interactive) {
    return undefined;
  }
  return isSelected.value ? `Deselect ${props.optionName}` : `Select ${props.optionName}`;
});

const isButton = computed(() => props.interactive && props.as === 'button');

const toggle = () => {
  if (props.disabled || !props.interactive) {
    return;
  }
  const next = !isSelected.value;
  if (!isControlled.value) {
    internalSelected.value = next;
  }
  emit('update:selected', next);
};
</script>

<template>
  <Primitive
    :as="props.as"
    class="chip-select"
    :class="chipSelectRecipe({
      size: props.size,
      interactive: props.interactive,
      selected: isSelected,
      disabled: props.disabled,
      elevated: props.elevated,
    })"
    :type="isButton ? 'button' : undefined"
    :disabled="isButton ? props.disabled : undefined"
    :role="props.interactive && !isButton ? 'button' : undefined"
    :aria-pressed="props.interactive ? isSelected : undefined"
    :aria-disabled="props.interactive && props.disabled ? true : undefined"
    :aria-label="ariaLabel"
    :tabindex="props.interactive ? (props.disabled ? -1 : 0) : undefined"
    @click="toggle"
  >
    <span
      class="chip-select__leading"
      :class="chipSelectLeadingRecipe({ variant: leadingVariant })"
      :aria-hidden="isLeadingDecorative ? 'true' : undefined"
    >
      <img
        v-if="leadingVariant === 'avatar'"
        :src="props.leadingAvatarSrc"
        :alt="props.leadingAvatarAlt ?? ''"
        :class="chipSelectAvatar"
        loading="lazy"
        decoding="async"
      />
      <IconUi v-else-if="leadingVariant === 'icon'" :leading-icon="props.leadingIcon" />
    </span>

    <span class="chip-select__content" :class="chipSelectContentRecipe({ size: props.size })">
      <span class="chip-select__label" :class="chipSelectLabel">
        <slot />
      </span>
      <span
        v-if="hasSubLabel"
        class="chip-select__sublabel"
        :class="chipSelectSubLabelRecipe({ disabled: props.disabled })"
      >
        <slot name="sublabel">{{ props.subLabel }}</slot>
      </span>
    </span>
  </Primitive>
</template>
